// Form utilities for the Forms feature

const FORMS_STORAGE_KEY = 'progress-admin-forms';

// Local (localStorage / UI) form shapes — these are the backward-compat client-side
// forms, distinct from the API DTOs in ../api/generated (which model server forms).
interface FormQuestion {
    id: string;
    [key: string]: unknown;
}

interface FormRecipient {
    answered?: boolean;
    [key: string]: unknown;
}

interface StoredForm {
    id: string;
    title: string;
    logoUrl: string | null;
    questions: FormQuestion[];
    createdAt: string;
    lastUpdated: string;
    recipients: FormRecipient[];
}

interface RecipientStats {
    total: number;
    answered: number;
    notAnswered: number;
}

/**
 * Generate a unique ID
 */
export const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get the current date in YYYY-MM-DD format, in the USER'S LOCAL timezone.
 *
 * Deliberately not `toISOString().split('T')[0]`: that converts to UTC first, so for any user
 * east of UTC (Israel is UTC+2/+3) the date between local midnight and 02:00/03:00 is still
 * YESTERDAY. A "today" presented to a user — a form's created date, a body-measurement date — is
 * a LOCAL calendar day, and a calendar day must never be routed through a timezone conversion.
 */
export const getCurrentDate = (): string => {
    return toLocalDateString(new Date());
};

/**
 * Render an epoch-SECONDS instant from the server as a YYYY-MM-DD day in the USER'S LOCAL zone.
 *
 * The server sends a true UTC instant and each client localizes it. Formatting with
 * `toISOString()` renders it in UTC instead, so an instant at 01:00 Israel (22:00 UTC the day
 * before) displayed as the PREVIOUS day.
 */
export const epochSecondsToLocalDate = (epochSeconds?: number | null): string => {
    if (epochSeconds == null) return '';
    return toLocalDateString(new Date(epochSeconds * 1000));
};

/** YYYY-MM-DD from a Date's LOCAL fields — never via toISOString(), which converts to UTC. */
const toLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Format date to Hebrew locale
 */
export const formatDateHebrew = (dateString?: string | number | null): string => {
    if (!dateString) return '';
    // A bare "yyyy-MM-dd" is a CALENDAR DAY, and `new Date(str)` parses that as UTC midnight —
    // toLocaleDateString would then render the PREVIOUS day for any viewer west of UTC (verified:
    // "2026-07-15" showed as 14.07.2026 in America/New_York). Build it from the parts instead, so
    // no timezone is involved. Timestamps and full ISO instants still go through Date, where
    // localizing IS correct.
    const calendarDayMatch =
        typeof dateString === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString) : null;
    const date = calendarDayMatch
        ? new Date(Number(calendarDayMatch[1]), Number(calendarDayMatch[2]) - 1, Number(calendarDayMatch[3]))
        : new Date(dateString);
    return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

/**
 * Get forms from localStorage
 */
export const getFormsFromStorage = (): StoredForm[] => {
    try {
        const stored = localStorage.getItem(FORMS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error reading forms from storage:', error);
        return [];
    }
};

/**
 * Save forms to localStorage
 */
export const saveFormsToStorage = (forms: StoredForm[]): boolean => {
    try {
        localStorage.setItem(FORMS_STORAGE_KEY, JSON.stringify(forms));
        return true;
    } catch (error) {
        console.error('Error saving forms to storage:', error);
        return false;
    }
};

/**
 * Create a new empty form
 */
export const createEmptyForm = (): StoredForm => {
    const now = getCurrentDate();
    return {
        id: `form-${generateId()}`,
        title: 'טופס חדש',
        logoUrl: null,
        questions: [],
        createdAt: now,
        lastUpdated: now,
        recipients: []
    };
};

/**
 * Initialize forms - returns forms from storage
 * Note: Form data now comes from API, this is kept for backward compatibility
 */
export const initializeForms = (): StoredForm[] => {
    return getFormsFromStorage();
};

/**
 * Duplicate a question with new ID
 */
export const duplicateQuestion = <T extends FormQuestion>(question: T): T => {
    return {
        ...question,
        id: `question-${generateId()}`
    };
};

/**
 * Count recipients by status
 */
export const getRecipientStats = (recipients?: FormRecipient[] | null): RecipientStats => {
    if (!recipients || recipients.length === 0) {
        return { total: 0, answered: 0, notAnswered: 0 };
    }

    const answered = recipients.filter(r => r.answered).length;
    return {
        total: recipients.length,
        answered,
        notAnswered: recipients.length - answered
    };
};
